import React from 'react';
import { Folder } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { Text } from '@cotera/client/app/components/ui';
import { ResourceListSecondaryCells } from '../resources/list/resource-list-view';
import { ResourceScopeBadge } from '../resources/list/resource-scope-badge';
import { ResourceCreatorCell } from '../resources/list/resource-creator-cell';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  CELL_LABEL_CREATED_BY,
  RESOURCE_LIST_CELL_TEXT,
  type FolderScope,
} from '../resources/list/resource-list-types';
import {
  RESOURCE_TYPE_CONFIG,
  type NonFolderResourceType,
} from '../resources/type/resource-type-config';
import type { WorkflowSummary } from '../workflow/use-workflow-summaries';
import {
  isVirtualChatsItem,
  type FolderContentsListItem,
} from './folder-contents-list-items';
import { getLibraryItemDisplay } from './library-item-display';
import { FavoriteRowMarker } from '../resources/favorites';

/**
 * Human label for a row's kind. Not rendered as a column in either view — the
 * colour-coded icon tile carries the type visually — but the tile has no
 * accessible name, so both the row and the card expose this as screen-reader-only
 * text. Without it an agent, a document and a workflow are structurally
 * identical to a screen reader.
 */
export function itemTypeLabel(item: FolderContentsListItem): string {
  if (isVirtualChatsItem(item)) {
    return 'Chats';
  }
  if (item.type === 'folder') {
    return 'Folder';
  }
  const resourceType = item.type as NonFolderResourceType;
  return RESOURCE_TYPE_CONFIG[resourceType]?.label ?? 'Item';
}

export type LibraryRowProps = {
  item: FolderContentsListItem;
  userId: string;
  nestedFolderScope: FolderScope | null;
  isTopLevel: boolean;
  workflowSummaries: Map<string, WorkflowSummary>;
  dateField: 'updatedAt' | 'createdAt';
  /**
   * Children this expanded folder holds beyond the single page inline expansion
   * fetches. Non-zero means the rows disclosed under it are a prefix, not the
   * whole folder, so the row says so rather than reading as complete.
   */
  hiddenChildCount?: number;
};

/**
 * One Library row: name (+ unread dot), a subheading carrying whatever
 * secondary signal the type has, then the creator, scope and date columns.
 *
 * Descriptions are deliberately left to {@link LibraryCard}: a row's subheading
 * is one short line, where a description would displace the item's location.
 */
export const LibraryRow: React.FC<LibraryRowProps> = ({
  item,
  userId,
  nestedFolderScope,
  isTopLevel,
  workflowSummaries,
  dateField,
  hiddenChildCount = 0,
}) => {
  const {
    name,
    summary,
    scope,
    users,
    ownerId,
    creatorId,
    dateLabel,
    hasUnreadAssistant,
  } = getLibraryItemDisplay({
    item,
    userId,
    nestedFolderScope,
    isTopLevel,
    workflowSummaries,
    dateField,
  });

  return (
    <>
      <div className="min-w-0 w-full @list-cols/list:flex-1 @list-cols/list:min-w-0">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            {/* `title` on the wrapper, not the paragraph: `Text.P` takes no DOM
                attributes, and a truncated name is otherwise unreadable.

                The star lives *inside* this wrapper so it trails the name's
                last character. The wrapper is what holds `flex-1`, so a star
                outside it was pushed to the far edge of the name column, a
                whole column's width away from the row it marks. `min-w-0` on
                the paragraph is what lets it shrink and ellipsise, leaving the
                star tucked against the truncation rather than off-screen. */}
            <div
              className="flex min-w-0 flex-1 items-center gap-1.5"
              title={name}
            >
              <Text.P className="min-w-0 truncate font-medium text-slate-800">
                {name}
              </Text.P>
              {isVirtualChatsItem(item) ? null : (
                <FavoriteRowMarker
                  type={item.type as ResourceType}
                  id={item.data.id}
                />
              )}
            </div>
            {/* The type has no column any more; the icon tile that replaces it
                is unlabelled colour and glyph, so the row says it out loud. */}
            <span className="sr-only">{itemTypeLabel(item)}</span>
            {hasUnreadAssistant ? (
              <span
                className="h-2 w-2 rounded-full bg-primary shrink-0"
                aria-label="Unread assistant message"
              />
            ) : null}
          </div>
          {summary !== null || hiddenChildCount > 0 ? (
            // Full `text-muted-foreground`, not `/50`: at 50% opacity this text
            // sat near 1.7:1 against the page, well under the 4.5:1 needed at
            // this size — and it carries real content (run health, location).
            <p className="text-[11px] text-muted-foreground mt-0.5 flex min-w-0 items-center gap-1.5">
              {summary === null ? null : summary.kind === 'run' ? (
                <span className="flex shrink-0 items-center gap-1">
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${summary.dotClass}`}
                    aria-hidden="true"
                  />
                  <span className="whitespace-nowrap">{summary.text}</span>
                </span>
              ) : summary.kind === 'location' ? (
                <span className="flex min-w-0 items-center gap-1">
                  <Folder className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">{summary.text}</span>
                </span>
              ) : (
                <span className="truncate" title={summary.text}>
                  {summary.text}
                </span>
              )}
              {hiddenChildCount > 0 ? (
                // Expanding shows one page; without this the row's own "N items"
                // count contradicts the rows underneath it for no visible reason.
                <span
                  className="shrink-0 whitespace-nowrap"
                  title={`Expanding shows the first page only — open the folder to see the remaining ${hiddenChildCount}.`}
                >
                  {summary === null ? '' : '· '}
                  {hiddenChildCount} not shown
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      </div>
      {/* Fixed widths only once the *list* is wide enough for columns — see
          `--container-list-cols`. These cells are `shrink-0` and total ~430px, so
          while this switched on the viewport a Library rendered in the chat's
          artifact panel committed all of it regardless of the room it had, and
          the name (the one cell that gives) was left wrapping one character per
          line. Below the threshold `ResourceListSecondaryCells` puts them on
          their own line under the name instead. */}
      <ResourceListSecondaryCells>
        {/* `max-w-full` matters only in the stacked layout, where this cell has
            no width of its own and `shrink-0` stops it giving any back: a long
            name ran off the right edge of a narrow list instead of ellipsising.
            In column mode `w-[140px]` is the smaller of the two and wins. */}
        <div
          className={cn(
            'min-w-0 max-w-full shrink-0 text-left @list-cols/list:pl-3 @list-cols/list:w-[140px]',
            CELL_LABEL_CREATED_BY
          )}
        >
          <ResourceCreatorCell creatorId={creatorId} currentUserId={userId} />
        </div>
        {/* No "Shared with" label, stacked or otherwise: the badge leads with
            a lock / globe / people glyph that says the same thing. Two labelled
            pairs on one line — "Created by <name>" beside "Shared with <name>" —
            was the thing readers could not take apart. */}
        <div className="shrink-0 text-left @list-cols/list:pl-3 @list-cols/list:w-[150px]">
          {/* Attribution moved to its own column, so this cell is now purely
              about who can see the item — including on inbound shares, which
              previously traded their scope away to name their owner. */}
          {scope !== null ? (
            <div className="flex">
              <ResourceScopeBadge
                scope={scope}
                users={users}
                ownerId={ownerId}
              />
            </div>
          ) : null}
        </div>
        {/* No label. Stacked, a bare date at the end of the line is
            unambiguous, and "Updated" in front of every row's date would be the
            longest label on the line for the least information. */}
        {/* No `tabular-nums`. Fixed-width figures exist to line digits up under
            each other, which was the point while this column was right-aligned;
            left-aligned there is nothing to line up. They are wider than the
            proportional figures the rest of the row uses, which is what made
            this cell read as a larger type than the creator and sharing cells
            beside it — it is 11px, exactly as they are. */}
        <div className="shrink-0 text-left @list-cols/list:pl-3 @list-cols/list:w-[100px]">
          <span className={cn(RESOURCE_LIST_CELL_TEXT, 'whitespace-nowrap')}>
            {dateLabel}
          </span>
        </div>
      </ResourceListSecondaryCells>
    </>
  );
};
